import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Edit2, Trash2, X, Save, ChevronRight, ChevronDown, Download, RefreshCw, Upload,
} from 'lucide-react';
import { useApi } from '../auth/useApi';
import { SoftwareImportPanel } from '../components/SoftwareImportPanel';

/**
 * Applications — spend + licensing view over the software catalog (v1.29.1).
 *
 * Role-aware STRUCTURALLY: the backend strips financial fields from the
 * spend-report payload for techs, so this page renders money columns, spend
 * KPIs, and cost inputs only when those keys actually exist in the response.
 * There is no client-side role logic to get wrong — a tech's payload simply
 * produces the licensing view, and the CSV (built from the same payload)
 * can't contain money either.
 */

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

interface Me {
  userId: number;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface AppRow {
  softwareCatalogId: number;
  name: string;
  category: string | null;
  vendor: string | null;
  tracked: boolean;
  purchased: number | null;
  assigned: number;
  assignedDeparted: number;
  unassigned: number | null;
  utilization: number | null;
  renewalDate: string | null;
  seatCountSource: string;
  lastSyncedAt: string | null;
  ownerUserId: number | null;
  ownerName: string | null;
  appMasterUserId: number | null;
  appMasterName: string | null;
  vendorContactName: string | null;
  vendorContactEmail: string | null;
  vendorContactPhone: string | null;
  scopeMode: 'org' | 'stores';
  m365SkuId: string | null;
  holderIds?: number[];
  // admin-only (absent for techs — the server strips them)
  billingCycle?: 'monthly' | 'annual' | null;
  flatCost?: number | null;
  costPerLicense?: number | null;
  monthlyCost?: number | null;
  wastedMonthly?: number | null;
}

// ============================================================================
// Holder cards with store tags (v1.38). One fetch per expansion; each card
// shows the holder's store chips plus a compact "+ store…" dropdown.
// ============================================================================
const HoldersWithStores: React.FC<{
  api: any; catId: number; holders: Holder[];
  onRemove: (h: Holder, catId: number) => void;
}> = ({ api, catId, holders, onRemove }) => {
  const [stores, setStores] = useState<{ StoreId: number; Name: string }[]>([]);
  const [holderStores, setHolderStores] = useState<Record<number, number[]>>({});
  const [pickerFor, setPickerFor] = useState<number | null>(null);

  const load = async () => {
    try {
      const d: any = await api.get(`/api/users/software-catalog/${catId}/assignments`);
      setStores(Array.isArray(d?.stores) ? d.stores : []);
      const m: Record<number, number[]> = {};
      for (const hs of (Array.isArray(d?.holderStores) ? d.holderStores : [])) {
        (m[hs.SoftwareId] = m[hs.SoftwareId] || []).push(hs.StoreId);
      }
      setHolderStores(m);
    } catch { /* best-effort; the cards still render without store data */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [catId, holders.length]);

  const addStore = async (softwareId: number, storeId: number) => {
    try {
      await api.post(`/api/users/software-catalog/${catId}/assignments/${softwareId}/stores`, { storeId });
      setHolderStores({ ...holderStores, [softwareId]: [...(holderStores[softwareId] || []), storeId] });
    } catch (e: any) { alert(`Failed to add store: ${e?.message}`); }
  };
  const removeStore = async (softwareId: number, storeId: number) => {
    try {
      await api.del(`/api/users/software-catalog/${catId}/assignments/${softwareId}/stores/${storeId}`);
      setHolderStores({ ...holderStores, [softwareId]: (holderStores[softwareId] || []).filter(x => x !== storeId) });
    } catch (e: any) { alert(`Failed to remove store: ${e?.message}`); }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {holders.map(h => {
        const mine = holderStores[h.SoftwareId] || [];
        return (
          <div key={h.SoftwareId} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
            background: C.surface, borderRadius: 9, fontSize: 12.5,
            border: `1px solid ${h.Active ? C.border : C.warn}`,
          }}>
            <div>
              <div style={{ fontWeight: 700, color: C.text }}>{h.DisplayName}</div>
              <div style={{ fontSize: 11, color: h.Active ? C.textMuted : C.warn, fontWeight: h.Active ? 400 : 700 }}>
                {h.Active ? `assigned ${dateStr(h.AddedAt)}` : 'DEPARTED — seat reclaimable'}
              </div>
              {stores.length > 0 && (
                <button style={{
                          marginTop: 4, fontSize: 10.5, padding: '2px 9px', borderRadius: 6, cursor: 'pointer',
                          border: `1px solid ${C.border}`, background: 'transparent',
                          color: mine.length ? C.primary : C.textMuted, fontWeight: mine.length ? 700 : 400,
                        }}
                        onClick={() => setPickerFor(h.SoftwareId)}>
                  Stores ({mine.length})
                </button>
              )}
            </div>
            <button style={{ ...btnStyle, padding: '4px 9px', fontSize: 11.5, color: h.Active ? C.text : C.warn }}
                    onClick={() => onRemove(h, catId)}>
              {h.Active ? 'Remove' : 'Reclaim seat'}
            </button>
          </div>
        );
      })}
      {pickerFor !== null && (() => {
        const h = holders.find(x => x.SoftwareId === pickerFor);
        if (!h) return null;
        const mine = holderStores[pickerFor] || [];
        return (
          <div onClick={() => setPickerFor(null)}
               style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div onClick={e => e.stopPropagation()}
                 style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
                          padding: '18px 20px', width: 360, maxHeight: '80vh', overflowY: 'auto',
                          boxShadow: '0 8px 30px rgba(0,0,0,.25)' }}>
              <div style={{ fontWeight: 800, color: C.text, fontSize: 15, marginBottom: 2 }}>{h.DisplayName}</div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
                Store access · {mine.length} of {stores.length}
              </div>
              <div style={{ display: 'grid', gap: 6, marginBottom: 16 }}>
                {stores.map(s => {
                  const on = mine.includes(s.StoreId);
                  return (
                    <label key={s.StoreId}
                           style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13,
                                    color: C.text, cursor: 'pointer', padding: '3px 2px' }}>
                      <input type="checkbox" checked={on}
                             onChange={() => on ? removeStore(h.SoftwareId, s.StoreId) : addStore(h.SoftwareId, s.StoreId)} />
                      {s.Name}
                    </label>
                  );
                })}
              </div>
              <button style={{ ...btnStyle, padding: '7px 16px', fontSize: 13 }}
                      onClick={() => setPickerFor(null)}>Done</button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

interface Holder {
  SoftwareId: number;
  UserId: number;
  DisplayName: string;
  Email: string | null;
  Active: boolean;
  AddedAt: string | null;
}

interface UserOpt { UserId: number; DisplayName: string; Active?: boolean }

interface DiscoveredVendor {
  vendorName: string; acctNumber: string; firstSeen: string; lastSeen: string;
  months: number; total: number; avgMonthly: number | null;
}
interface Insights {
  lastSync: string | null;
  lastFullMonth: string;
  actualsByCatalog: { softwareCatalogId: number; lastMonthActual: number; trailing3Avg: number }[];
  discovered: DiscoveredVendor[];
  journalResiduals: { acctNumber: string; lastMonthAmount: number }[];
  series?: { softwareCatalogId: number; points: { month: string; amount: number }[] }[];
}

/** Tiny inline trend line for the actuals column (13 monthly points max). */
function Sparkline({ points, width = 90, height = 20 }: {
  points?: { month: string; amount: number }[]; width?: number; height?: number;
}) {
  if (!points || points.length < 2) return null;
  const vals = points.map(p => p.amount);
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals);
  if (max === min) return null;
  const step = width / (points.length - 1);
  const y = (v: number) => height - 2 - ((v - min) / (max - min)) * (height - 4);
  const d = vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block', marginTop: 3 }}>
      <path d={d} fill="none" stroke="#7a9b7e" strokeWidth={1.5} />
      <circle cx={(points.length - 1) * step} cy={y(vals[vals.length - 1])} r={2} fill="#2f6b50" />
    </svg>
  );
}

const money = (n: number | null | undefined) =>
  n == null ? '—' : '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateStr = (d: string | null | undefined) => (d ? String(d).slice(0, 10) : '—');
const daysUntil = (d: string | null | undefined) => {
  if (!d) return null;
  return Math.ceil((new Date(String(d).slice(0, 10)).getTime() - Date.now()) / 86_400_000);
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${C.border}`,
  background: C.bg, color: C.text, fontSize: 13, boxSizing: 'border-box',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
  textTransform: 'uppercase', color: C.textMuted, margin: '10px 0 4px',
};
const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface,
  color: C.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

export function Applications({ me }: { me: Me }) {
  const api = useApi();
  const [rows, setRows] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [fPerson, setFPerson] = useState('');
  const [fVendor, setFVendor] = useState('');
  const [fCategory, setFCategory] = useState('');
  const [fMaster, setFMaster] = useState('');
  const [fRenew90, setFRenew90] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [holders, setHolders] = useState<Holder[]>([]);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [skus, setSkus] = useState<{ SkuId: string; SkuPartNumber: string; PrepaidUnits: number; ConsumedUnits: number }[]>([]);
  const [users, setUsers] = useState<UserOpt[]>([]);
  const [assignPick, setAssignPick] = useState('');
  const [editing, setEditing] = useState<AppRow | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);
  const [swImport, setSwImport] = useState(false);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [mapPick, setMapPick] = useState<Record<string, string>>({});
  const [pendingMapVendor, setPendingMapVendor] = useState<string | null>(null);

  // Money fields exist only if the server sent them (admin). Everything
  // financial on this page keys off this — not off me.role.
  const hasMoney = useMemo(() => rows.some(r => 'monthlyCost' in r), [rows]);

  const load = async () => {
    try {
      setError(null);
      const j = await api.get<{ applications: AppRow[] }>('/api/users/software-catalog/spend-report');
      setRows(j.applications);
      // v1.40.1: load users up front (incl. inactive) so the filter bar's
      // person dropdown shows names from first paint and resolves departed
      // holders. Best-effort — a failure here must not break the page.
      api.get<UserOpt[]>('/api/users?includeInactive=1').then(setUsers).catch(() => {});
      try {
        setInsights(await api.get<Insights>('/api/users/software-catalog/spend-insights'));
      } catch { setInsights(null); } // techs get 403 — the page simply has no actuals surfaces
    } catch (e: any) {
      setError(e?.message ?? 'load failed');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let out = showAll ? rows : rows.filter(r => r.tracked);
    if (fPerson) out = out.filter(r => (r.holderIds ?? []).includes(+fPerson));
    if (fVendor) out = out.filter(r => r.vendor === fVendor);
    if (fCategory) out = out.filter(r => r.category === fCategory);
    if (fMaster) out = out.filter(r => r.appMasterUserId === +fMaster);
    if (fRenew90) {
      const cut = new Date(); cut.setDate(cut.getDate() + 90);
      const cutIso = cut.toISOString().slice(0, 10);
      out = out.filter(r => r.renewalDate != null && r.renewalDate <= cutIso);
    }
    return out;
  }, [rows, showAll, fPerson, fVendor, fCategory, fMaster, fRenew90]);

  const kpi = useMemo(() => {
    const tracked = rows.filter(r => r.tracked);
    const seatRows = tracked.filter(r => r.purchased != null && r.purchased > 0);
    const purchased = seatRows.reduce((s, r) => s + (r.purchased ?? 0), 0);
    const assigned = seatRows.reduce((s, r) => s + r.assigned, 0);
    return {
      tracked: tracked.length,
      utilization: purchased ? Math.round((assigned / purchased) * 100) : null,
      unassigned: seatRows.reduce((s, r) => s + (r.unassigned ?? 0), 0),
      departed: tracked.reduce((s, r) => s + r.assignedDeparted, 0),
      renewals90: tracked.filter(r => { const d = daysUntil(r.renewalDate); return d != null && d >= 0 && d <= 90; }).length,
      monthly: hasMoney ? tracked.reduce((s, r) => s + (r.monthlyCost ?? 0), 0) : null,
      wasted: hasMoney ? tracked.reduce((s, r) => s + (r.wastedMonthly ?? 0), 0) : null,
    };
  }, [rows, hasMoney]);

  const loadHolders = async (id: number) => {
    setHoldersLoading(true);
    try {
      const j = await api.get<{ users: Holder[] }>(`/api/users/software-catalog/${id}/assignments`);
      setHolders(j.users);
      if (users.length === 0) {
        try { const u = await api.get<UserOpt[]>('/api/users'); setUsers(u.filter(x => x.Active !== false)); } catch { /* ignore */ }
      }
    } finally {
      setHoldersLoading(false);
    }
  };

  const toggleExpand = async (id: number) => {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id); setHolders([]); setAssignPick('');
    await loadHolders(id);
  };

  const removeAssignment = async (h: Holder, appId: number) => {
    if (!window.confirm(`Remove this ${h.Active ? 'assignment' : 'departed-user seat'} from ${h.DisplayName}?`)) return;
    await api.del(`/api/users/${h.UserId}/software/${h.SoftwareId}`);
    await Promise.all([loadHolders(appId), load()]);
  };

  const assignTo = async (row: AppRow) => {
    if (!assignPick) return;
    await api.post(`/api/users/${assignPick}/software`, { name: row.name });
    setAssignPick('');
    await Promise.all([loadHolders(row.softwareCatalogId), load()]);
  };

  const del = async (row: AppRow) => {
    if (!window.confirm(`Delete "${row.name}" from the catalog? User assignments are kept but orphaned.`)) return;
    await api.del(`/api/users/software-catalog/${row.softwareCatalogId}`);
    await load();
  };

  const exportCsv = () => {
    const preferred = ['name','category','vendor','purchased','assigned','assignedDeparted','unassigned','utilization',
      'renewalDate','billingCycle','flatCost','costPerLicense','monthlyCost','wastedMonthly','seatCountSource','ownerName',
      'appMasterName','vendorContactName','vendorContactEmail','vendorContactPhone'];
    const present = new Set<string>();
    visible.forEach(r => Object.keys(r).forEach(k => present.add(k)));
    const cols = preferred.filter(c => present.has(c));
    const esc = (v: any) => v == null ? '' : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v);
    const csv = [cols.join(','), ...visible.map(r => cols.map(c => esc((r as any)[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'applications.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ---------------- add / edit modal ----------------
  const [f, setF] = useState<any>({});
  const openModal = (row: AppRow | null) => {
    setEditing(row); setModalErr(null);
    setF(row ? {
      name: row.name, category: row.category ?? '', vendor: row.vendor ?? '',
      billingCycle: row.billingCycle ?? '', flatCost: row.flatCost ?? '',
      costPerLicense: row.costPerLicense ?? '', purchased: row.purchased ?? '',
      renewalDate: row.renewalDate ? String(row.renewalDate).slice(0, 10) : '',
      ownerUserId: row.ownerUserId ?? '',
      appMasterUserId: row.appMasterUserId ?? '',
      vendorContactName: row.vendorContactName ?? '',
      vendorContactEmail: row.vendorContactEmail ?? '',
      vendorContactPhone: row.vendorContactPhone ?? '',
      scopeMode: row.scopeMode ?? 'org',
      m365SkuId: row.m365SkuId ?? '',
    } : { name: '', category: '', vendor: '', billingCycle: '', flatCost: '', costPerLicense: '', purchased: '', renewalDate: '', ownerUserId: '',
          appMasterUserId: '', vendorContactName: '', vendorContactEmail: '', vendorContactPhone: '', scopeMode: 'org', m365SkuId: '' });
    if (skus.length === 0) {
      api.get('/api/users/software-catalog/m365-skus').then((d: any) => setSkus(Array.isArray(d) ? d : [])).catch(() => {});
    }
    if (users.length === 0) {
      api.get<UserOpt[]>('/api/users').then(u => setUsers(u.filter(x => x.Active !== false))).catch(() => {});
    }
    setShowModal(true);
  };

  const save = async () => {
    if (!f.name?.trim()) { setModalErr('Name is required'); return; }
    setSaving(true); setModalErr(null);
    const num = (v: any) => (v === '' || v == null ? null : Number(v));
    const body: any = {
      name: f.name.trim(),
      category: f.category?.trim() || null,
      vendor: f.vendor?.trim() || null,
      purchasedLicenseCount: num(f.purchased),
      renewalDate: f.renewalDate || null,
      ownerUserId: num(f.ownerUserId),
      appMasterUserId: num(f.appMasterUserId),
      vendorContactName: f.vendorContactName?.trim() || null,
      vendorContactEmail: f.vendorContactEmail?.trim() || null,
      vendorContactPhone: f.vendorContactPhone?.trim() || null,
      scopeMode: f.scopeMode || 'org',
      m365SkuId: f.m365SkuId || null,
    };
    if (hasMoney || !editing) {
      // cost inputs are only shown when the server exposes money; send them
      // only in that case (or on create, where the form may include them)
      body.billingCycle = f.billingCycle || null;
      body.price = num(f.flatCost);
      body.pricePerLicense = num(f.costPerLicense);
    }
    try {
      if (editing) {
        await api.patch(`/api/users/software-catalog/${editing.softwareCatalogId}`, body);
      } else {
        const created = await api.post<{ softwareCatalogId: number }>('/api/users/software-catalog', body);
        if (pendingMapVendor && created?.softwareCatalogId) {
          await api.post('/api/users/software-catalog/vendor-map',
            { vendorName: pendingMapVendor, softwareCatalogId: created.softwareCatalogId });
        }
      }
      setPendingMapVendor(null);
      setShowModal(false);
      await load();
    } catch (e: any) {
      setModalErr(e?.message ?? 'save failed');
    } finally {
      setSaving(false);
    }
  };

  const actualsById = useMemo(() => {
    const m: Record<number, { lastMonthActual: number; trailing3Avg: number }> = {};
    for (const a of insights?.actualsByCatalog ?? []) m[a.softwareCatalogId] = a;
    return m;
  }, [insights]);

  const seriesById = useMemo(() => {
    const m: Record<number, { month: string; amount: number }[]> = {};
    for (const s2 of insights?.series ?? []) m[s2.softwareCatalogId] = s2.points;
    return m;
  }, [insights]);

  // v1.40: an app billed annually shows a flat monthly series (usually $0
  // between invoices). Detect "no movement across the last 3 points but real
  // spend somewhere in the window" and render calendar-year bars instead of a
  // misleading empty sparkline. Dormant apps (flat AND all-zero) stay flat.
  const annualById = useMemo(() => {
    const out: Record<number, { year: string; total: number }[] | null> = {};
    for (const [id, pts] of Object.entries(seriesById)) {
      const points = pts ?? [];
      const last3 = points.slice(-3).map(p => p.amount);
      const flat3 = last3.length >= 2 && Math.max(...last3) - Math.min(...last3) < 0.01;
      const anySpend = points.some(p => Math.abs(p.amount) > 0.01);
      if (flat3 && anySpend) {
        const byYear: Record<string, number> = {};
        for (const p of points) { const y = p.month.slice(0, 4); byYear[y] = (byYear[y] ?? 0) + p.amount; }
        out[+id] = Object.entries(byYear).map(([year, total]) => ({ year, total })).sort((a, b) => a.year.localeCompare(b.year));
      } else {
        out[+id] = null;
      }
    }
    return out;
  }, [seriesById]);

  const discoveredGroups = useMemo(() => {
    const g: Record<string, { vendorName: string; total: number; months: number; accts: string[]; firstSeen: string }> = {};
    for (const d2 of insights?.discovered ?? []) {
      const cur = g[d2.vendorName] ?? { vendorName: d2.vendorName, total: 0, months: 0, accts: [], firstSeen: d2.firstSeen };
      cur.total += d2.total;
      cur.months = Math.max(cur.months, d2.months);
      if (!cur.accts.includes(d2.acctNumber)) cur.accts.push(d2.acctNumber);
      if (d2.firstSeen < cur.firstSeen) cur.firstSeen = d2.firstSeen;
      g[d2.vendorName] = cur;
    }
    return Object.values(g).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  }, [insights]);

  const mapVendor = async (vendorName: string, softwareCatalogId: number) => {
    await api.post('/api/users/software-catalog/vendor-map', { vendorName, softwareCatalogId });
    await load();
  };
  const ignoreVendor = async (vendorName: string) => {
    if (!window.confirm(`Ignore "${vendorName}"? It will no longer appear in discovered spend.`)) return;
    await api.post('/api/users/software-catalog/vendor-map', { vendorName, ignore: true });
    await load();
  };
  const createFromVendor = (vendorName: string) => {
    setEditing(null); setModalErr(null);
    setPendingMapVendor(vendorName);
    const cleaned = vendorName.replace(/,?\s+(inc\.?|llc\.?|corp\.?|ltd\.?)\s*$/i, '').trim();
    setF({ name: cleaned, category: '', vendor: vendorName, billingCycle: '', flatCost: '',
           costPerLicense: '', purchased: '', renewalDate: '', ownerUserId: '',
           appMasterUserId: '', vendorContactName: '', vendorContactEmail: '', vendorContactPhone: '', scopeMode: 'org', m365SkuId: '' });
    if (users.length === 0) {
      api.get<UserOpt[]>('/api/users').then(u => setUsers(u.filter(x => x.Active !== false))).catch(() => {});
    }
    setShowModal(true);
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading applications…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  const kpiCard = (label: string, value: React.ReactNode, hint: string, warn = false) => (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 16px', minWidth: 150, flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.textMuted }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: warn ? C.warn : C.primary, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: C.textMuted }}>{hint}</div>
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1250, margin: '0 auto' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 22, color: C.text }}>Applications</h1>
          <div style={{ color: C.textMuted, fontSize: 13, marginTop: 2 }}>
            {hasMoney
              ? 'Cost, seats, and renewals for every application IT manages. Open a row to see who holds its licenses.'
              : 'Seat counts, license holders, and cleanup signals. Open a row to see who holds its licenses.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.textMuted, cursor: 'pointer' }}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
            Show untracked
          </label>
          <button style={btnStyle} onClick={() => setSwImport(true)}><Upload size={15} /> Import</button>
          <button style={btnStyle} onClick={exportCsv}><Download size={15} /> CSV</button>
          <button style={btnStyle} onClick={() => load()}><RefreshCw size={15} /></button>
          <button style={{ ...btnStyle, background: C.primary, borderColor: C.primary, color: '#fff' }} onClick={() => openModal(null)}>
            <Plus size={15} /> Add application
          </button>
        </div>
      </div>

      {/* KPI band — money cards appear only when the payload carries money */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0 18px' }}>
        {hasMoney && kpiCard('Monthly spend', money(kpi.monthly), 'tracked apps, cycle-normalized')}
        {hasMoney && kpiCard('Wasted / mo', money(kpi.wasted), `${kpi.unassigned} unassigned seats`, (kpi.wasted ?? 0) > 0)}
        {kpiCard('Applications', kpi.tracked, 'tracked in the catalog')}
        {kpiCard('Utilization', kpi.utilization != null ? `${kpi.utilization}%` : '—', 'assigned ÷ purchased')}
        {!hasMoney && kpiCard('Unassigned seats', kpi.unassigned, 'purchased but not assigned', kpi.unassigned > 0)}
        {kpiCard('Departed seats', kpi.departed, 'held by deactivated accounts', kpi.departed > 0)}
        {kpiCard('Renewals · 90d', kpi.renewals90, 'coming up')}
      </div>

      {/* v1.40 filter bar — client-side over loaded rows; KPI band stays global */}
      {(() => {
        const holderName = (id: number) => (users.find(u => u.UserId === id)?.DisplayName) ?? `#${id}`;
        const personOpts = Array.from(new Set(rows.flatMap(r => (r.holderIds ?? []))))
          .map(id => ({ id, name: holderName(id) }))
          .sort((a, b) => a.name.localeCompare(b.name));
        const vendorOpts = Array.from(new Set(rows.map(r => r.vendor).filter(Boolean) as string[])).sort();
        const categoryOpts = Array.from(new Set(rows.map(r => r.category).filter(Boolean) as string[])).sort();
        const masterOpts = Array.from(new Set(rows.map(r => r.appMasterUserId).filter((x): x is number => x != null)))
          .map(id => ({ id, name: (rows.find(r => r.appMasterUserId === id)?.appMasterName) ?? holderName(id) }))
          .sort((a, b) => a.name.localeCompare(b.name));
        const active = fPerson || fVendor || fCategory || fMaster || fRenew90;
        const sel: React.CSSProperties = { ...inputStyle, padding: '5px 8px', fontSize: 12.5, minWidth: 130 };
        return (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 0 16px' }}>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: C.textMuted }}>Filter</span>
            <select style={sel} value={fPerson} onChange={e => setFPerson(e.target.value)}>
              <option value="">Person — anyone</option>
              {personOpts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select style={sel} value={fVendor} onChange={e => setFVendor(e.target.value)}>
              <option value="">Vendor — any</option>
              {vendorOpts.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select style={sel} value={fCategory} onChange={e => setFCategory(e.target.value)}>
              <option value="">Category — any</option>
              {categoryOpts.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select style={sel} value={fMaster} onChange={e => setFMaster(e.target.value)}>
              <option value="">App master — anyone</option>
              {masterOpts.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            <button onClick={() => setFRenew90(v => !v)}
                    style={{ ...sel, minWidth: 0, cursor: 'pointer', fontWeight: fRenew90 ? 700 : 400,
                             border: `1px solid ${fRenew90 ? C.warn : C.border}`,
                             background: fRenew90 ? 'rgba(196,121,58,.10)' : 'transparent',
                             color: fRenew90 ? C.warn : C.textMuted }}>
              Renewing ≤ 90 days
            </button>
            {active && (
              <>
                <span style={{ fontSize: 12, color: C.textMuted }}>{visible.length} of {rows.filter(r => showAll || r.tracked).length}</span>
                <button onClick={() => { setFPerson(''); setFVendor(''); setFCategory(''); setFMaster(''); setFRenew90(false); }}
                        style={{ ...sel, minWidth: 0, cursor: 'pointer', color: C.text }}>Clear</button>
              </>
            )}
          </div>
        );
      })()}
      {/* discovered spend review queue (admins only — gated on insights) */}
      {insights && discoveredGroups.length > 0 && (
        <div style={{ background: 'rgba(196,121,58,.08)', border: `1px solid ${C.warn}`, borderRadius: 12, padding: '14px 16px', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: C.warn }}>
              Discovered spend — needs review
            </span>
            <span style={{ background: C.warn, color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '1px 8px' }}>
              {discoveredGroups.length}
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>
            Vendors found in the IT expense accounts with no application mapped. Map each once — after that its spend updates automatically.
          </div>
          {discoveredGroups.map(dv => (
            <div key={dv.vendorName} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', marginTop: 6 }}>
              <div style={{ fontWeight: 700, minWidth: 160, color: C.text }}>{dv.vendorName}</div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                acct {dv.accts.join(', ')} · first seen {dv.firstSeen} · {dv.months} mo
              </div>
              <div style={{ fontWeight: 800, color: C.warn, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                {money(dv.months ? Math.round((dv.total / dv.months) * 100) / 100 : dv.total)} / mo avg
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                <button style={{ ...btnStyle, padding: '5px 10px', fontSize: 12, background: C.warn, borderColor: C.warn, color: '#fff' }}
                        onClick={() => createFromVendor(dv.vendorName)}>Create application</button>
                <select value={mapPick[dv.vendorName] ?? ''} onChange={e => setMapPick({ ...mapPick, [dv.vendorName]: e.target.value })}
                        style={{ ...inputStyle, width: 170, padding: '5px 8px', fontSize: 12 }}>
                  <option value="">Map to existing…</option>
                  {[...rows].sort((a, b) => a.name.localeCompare(b.name)).map(r2 => (
                    <option key={r2.softwareCatalogId} value={r2.softwareCatalogId}>{r2.name}</option>
                  ))}
                </select>
                <button style={{ ...btnStyle, padding: '5px 10px', fontSize: 12 }} disabled={!mapPick[dv.vendorName]}
                        onClick={() => mapVendor(dv.vendorName, +mapPick[dv.vendorName])}>Map</button>
                <button style={{ ...btnStyle, padding: '5px 10px', fontSize: 12, color: C.textMuted }}
                        onClick={() => ignoreVendor(dv.vendorName)}>Ignore</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {insights && (
        <div style={{ fontSize: 12, color: C.textMuted, margin: '-6px 0 12px' }}>
          Spend actuals: NetSuite AP via Definite{insights.lastSync ? ` · synced ${String(insights.lastSync).slice(0, 16).replace('T', ' ')} UTC` : ''} · last full month {insights.lastFullMonth}
        </div>
      )}

      {/* table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
          <thead>
            <tr>
              {['Application', 'Vendor', 'Seats · assigned / purchased', ...(hasMoney ? ['Cost / seat', 'Monthly'] : []), ...(insights ? ['Last mo actual'] : []), 'Renewal', 'Source', ''].map((h, i) => (
                <th key={i} style={{ textAlign: i >= 3 && i <= (hasMoney ? 4 : 2) && hasMoney ? 'right' : 'left', padding: '10px 12px', background: C.bg, color: C.textMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={9} style={{ padding: 30, color: C.textMuted, textAlign: 'center' }}>
                No tracked applications yet — Add application, or tick “Show untracked” to see the full catalog.
              </td></tr>
            )}
            {visible.map(r => {
              const d = daysUntil(r.renewalDate);
              const renewSoon = d != null && d >= 0 && d <= 60;
              const renewPast = d != null && d < 0;
              const isOpen = expanded === r.softwareCatalogId;
              return (
                <React.Fragment key={r.softwareCatalogId}>
                  <tr style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }} onClick={() => toggleExpand(r.softwareCatalogId)}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: C.text }}>
                        {isOpen ? <ChevronDown size={15} color={C.secondary} /> : <ChevronRight size={15} color={C.secondary} />}
                        {r.name}
                      </div>
                      {r.category && <div style={{ fontSize: 11.5, color: C.textMuted, marginLeft: 21 }}>{r.category}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', color: C.textMuted, fontSize: 12.5 }}>{r.vendor ?? '—'}</td>
                    <td style={{ padding: '10px 12px', minWidth: 170 }}>
                      {r.purchased != null ? (
                        <>
                          <div style={{ fontSize: 12, color: C.textMuted }}>
                            {r.assigned} / {r.purchased}{r.utilization != null ? ` · ${r.utilization}%` : ''}
                          </div>
                          <div style={{ height: 6, borderRadius: 99, background: C.border, overflow: 'hidden', margin: '3px 0' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, r.utilization ?? 0)}%`, background: (r.utilization ?? 0) < 60 ? C.warn : C.secondary }} />
                          </div>
                          {(r.unassigned ?? 0) > 0 || r.assignedDeparted > 0 ? (
                            <div style={{ fontSize: 11.5, color: C.warn, fontWeight: 600 }}>
                              {(r.unassigned ?? 0) > 0 ? `${r.unassigned} unassigned` : ''}
                              {(r.unassigned ?? 0) > 0 && r.assignedDeparted > 0 ? ' · ' : ''}
                              {r.assignedDeparted > 0 ? `${r.assignedDeparted} departed` : ''}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <span style={{ fontSize: 12, color: C.textMuted }}>
                          {r.assigned > 0 ? `${r.assigned} assigned · ` : ''}no seat count
                        </span>
                      )}
                    </td>
                    {hasMoney && (
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {r.costPerLicense != null ? money(r.costPerLicense) : '—'}
                        {r.flatCost != null && <div style={{ fontSize: 11, color: C.textMuted }}>+ {money(r.flatCost)} flat</div>}
                      </td>
                    )}
                    {hasMoney && (
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {money(r.monthlyCost)}
                        {(r.wastedMonthly ?? 0) > 0 && <div style={{ fontSize: 11, color: C.warn, fontWeight: 600 }}>{money(r.wastedMonthly)} wasted</div>}
                      </td>
                    )}
                    {insights && (
                      <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {actualsById[r.softwareCatalogId] ? (
                          <>
                            {money(actualsById[r.softwareCatalogId].lastMonthActual)}
                            {(() => {
                              if (r.billingCycle !== 'monthly' || r.monthlyCost == null || r.monthlyCost <= 0) return null;
                              const act = actualsById[r.softwareCatalogId].lastMonthActual;
                              const gap = act - r.monthlyCost;
                              const material = Math.abs(gap) > Math.max(100, r.monthlyCost * 0.2);
                              if (!material) return null;
                              return gap > 0
                                ? <span title={`actual ${money(act)} vs entered ${money(r.monthlyCost)}`} style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: C.warn }}>▲ over</span>
                                : <span title={`actual ${money(act)} vs entered ${money(r.monthlyCost)} — cancelled, or entered cost stale?`} style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: C.textMuted }}>▼ under</span>;
                            })()}
                            <div style={{ fontSize: 11, color: C.textMuted }}>3-mo avg {money(actualsById[r.softwareCatalogId].trailing3Avg)}</div>
                            {annualById[r.softwareCatalogId] ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 22 }}>
                                  {(() => {
                                    const bars = annualById[r.softwareCatalogId]!;
                                    const max = Math.max(...bars.map(b => Math.abs(b.total)), 1);
                                    return bars.map(b => (
                                      <div key={b.year} title={`${b.year}: ${money(b.total)}`}
                                           style={{ width: 12, height: Math.max(2, Math.round((Math.abs(b.total) / max) * 22)),
                                                    background: C.primary, borderRadius: 2 }} />
                                    ));
                                  })()}
                                </div>
                                <span style={{ fontSize: 9.5, fontWeight: 700, color: C.textMuted, letterSpacing: '.03em' }}>billed annually</span>
                              </div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Sparkline points={seriesById[r.softwareCatalogId]} />
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: C.textMuted }}>—</span>
                        )}
                      </td>
                    )}
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: renewPast ? C.danger : renewSoon ? C.warn : C.text, fontWeight: renewSoon || renewPast ? 700 : 400 }}>
                      {dateStr(r.renewalDate)}{d != null && d >= 0 && d <= 90 ? <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 400 }}> · {d}d</span> : null}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px', border: `1px solid ${C.border}`, background: C.bg, color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {r.seatCountSource}{r.lastSyncedAt ? ` · ${dateStr(r.lastSyncedAt)}` : ''}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                      <button title="Edit" style={{ ...btnStyle, padding: 6 }} onClick={() => openModal(r)}><Edit2 size={14} /></button>{' '}
                      <button title="Delete" style={{ ...btnStyle, padding: 6, color: C.danger }} onClick={() => del(r)}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td colSpan={(hasMoney ? 8 : 6) + (insights ? 1 : 0)} style={{ background: C.bg, padding: '12px 16px 16px 38px' }}>
                        {(r.appMasterName || r.ownerName || r.vendorContactName || r.vendorContactEmail || r.vendorContactPhone) && (
                          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
                            {r.appMasterName && <>Application master: <b style={{ color: C.text }}>{r.appMasterName}</b></>}
                            {r.appMasterName && r.ownerName ? ' · ' : ''}
                            {r.ownerName && <>Business owner: <b style={{ color: C.text }}>{r.ownerName}</b></>}
                            {(r.vendorContactName || r.vendorContactEmail || r.vendorContactPhone) && (
                              <> · Vendor contact: <b style={{ color: C.text }}>{[r.vendorContactName, r.vendorContactEmail, r.vendorContactPhone].filter(Boolean).join(' · ')}</b></>
                            )}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.textMuted }}>
                            License holders · {holders.length}
                          </span>
                          <select value={assignPick} onChange={e => setAssignPick(e.target.value)}
                                  style={{ ...inputStyle, width: 220, padding: '5px 8px', fontSize: 12.5 }}>
                            <option value="">Assign to user…</option>
                            {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
                          </select>
                          <button style={{ ...btnStyle, padding: '5px 10px', fontSize: 12 }} disabled={!assignPick} onClick={() => assignTo(r)}>Assign</button>
                        </div>
                        {holdersLoading ? (
                          <div style={{ color: C.textMuted, fontSize: 12.5 }}>Loading…</div>
                        ) : holders.length === 0 ? (
                          <div style={{ color: C.textMuted, fontSize: 12.5 }}>No one is assigned this application.</div>
                        ) : (
                          <HoldersWithStores api={api} catId={r.softwareCatalogId} holders={holders} onRemove={removeAssignment} />
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10, fontSize: 11.5, color: C.textMuted }}>
        Seats: assigned counts come from user software assignments; “departed” = still assigned to an account the nightly
        directory sync has deactivated. {hasMoney ? 'Monthly = flat/plan cost + per-seat cost × purchased, cycle-normalized.' : ''}
        {insights && insights.journalResiduals.length > 0 && (
          <> Unattributed journal activity last month:{' '}
          {insights.journalResiduals.map(j2 => `${money(j2.lastMonthAmount)} (acct ${j2.acctNumber})`).join(', ')} — spend AP cannot tie to a vendor.</>
        )}
      </div>

      {swImport && (
        <SoftwareImportPanel api={api} onClose={() => setSwImport(false)} onChanged={load} />
      )}

      {/* add / edit modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}
             onClick={() => !saving && setShowModal(false)}>
          <div style={{ background: C.surface, borderRadius: 14, padding: 22, width: 520, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 17, color: C.text }}>{editing ? `Edit — ${editing.name}` : 'Add application'}</h2>
              <button style={{ ...btnStyle, marginLeft: 'auto', padding: 6, border: 'none' }} onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            {!editing && (
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4 }}>
                Adding an application with cost or vendor data also opens a “Add to Definite Tech Spend” ticket for the mapping work.
              </div>
            )}
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Category</label>
                <input style={inputStyle} value={f.category} onChange={e => setF({ ...f, category: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Vendor (AP name)</label>
                <input style={inputStyle} value={f.vendor} onChange={e => setF({ ...f, vendor: e.target.value })} />
              </div>
            </div>
            {(hasMoney || !editing) && (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Flat cost / cycle</label>
                  <input style={inputStyle} type="number" min="0" step="0.01" value={f.flatCost}
                         onChange={e => setF({ ...f, flatCost: e.target.value })} placeholder="plan / base fee" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Cost per seat / cycle</label>
                  <input style={inputStyle} type="number" min="0" step="0.01" value={f.costPerLicense}
                         onChange={e => setF({ ...f, costPerLicense: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Billing cycle</label>
                  <select style={inputStyle} value={f.billingCycle} onChange={e => setF({ ...f, billingCycle: e.target.value })}>
                    <option value="">—</option>
                    <option value="monthly">monthly</option>
                    <option value="annual">annual</option>
                  </select>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Seats purchased</label>
                <input style={inputStyle} type="number" min="0" step="1" value={f.purchased}
                       onChange={e => setF({ ...f, purchased: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Renewal date</label>
                <input style={inputStyle} type="date" value={f.renewalDate}
                       onChange={e => setF({ ...f, renewalDate: e.target.value })} />
              </div>
            </div>
            <label style={labelStyle}>Business owner</label>
            <select style={inputStyle} value={f.ownerUserId} onChange={e => setF({ ...f, ownerUserId: e.target.value })}>
              <option value="">— none (renewal alerts go to admins) —</option>
              {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
            </select>
            <label style={labelStyle}>Application master</label>
            <select style={inputStyle} value={f.appMasterUserId} onChange={e => setF({ ...f, appMasterUserId: e.target.value })}>
              <option value="">— none —</option>
              {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
            </select>
            <label style={labelStyle}>Microsoft 365 license link</label>
            <select style={inputStyle} value={f.m365SkuId ?? ''} onChange={e => setF({ ...f, m365SkuId: e.target.value })}>
              <option value="">— not linked · manual seats —</option>
              {skus.map(s => (
                <option key={s.SkuId} value={s.SkuId}>
                  {s.SkuPartNumber} · {s.ConsumedUnits}/{s.PrepaidUnits} assigned
                </option>
              ))}
            </select>
            <label style={labelStyle}>Vendor contact</label>
            <div style={{ display: 'flex', gap: 10 }}>
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Name" value={f.vendorContactName}
                     onChange={e => setF({ ...f, vendorContactName: e.target.value })} />
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Email" value={f.vendorContactEmail}
                     onChange={e => setF({ ...f, vendorContactEmail: e.target.value })} />
              <input style={{ ...inputStyle, flex: 1 }} placeholder="Phone" value={f.vendorContactPhone}
                     onChange={e => setF({ ...f, vendorContactPhone: e.target.value })} />
            </div>
            {modalErr && <div style={{ color: C.danger, fontSize: 12.5, marginTop: 10 }}>{modalErr}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button style={btnStyle} disabled={saving} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...btnStyle, background: C.primary, borderColor: C.primary, color: '#fff' }} disabled={saving} onClick={save}>
                <Save size={15} /> {saving ? 'Saving…' : editing ? 'Save changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
